import useAxios from 'axios-hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Filter, Home } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import classnames from 'classnames'
import { useTheme } from 'styled-components'
import _ from 'lodash'
import {
  Button,
  DropdownItem,
  DropdownMenu,
  InputGroup,
  Spinner,
  UncontrolledDropdown,
} from 'reactstrap'
import Checkbox from '~/components/@vuexy/checkbox/CheckboxesVuexy'
import Breadcrumb from '~/components/Breadcrumb'
import { Hide, Iterate, Show } from '~/components/commons'
import useWindowDimensions from '~/hooks/useWindowDimensions'

import NotificationsFilterPanel from './components/NotificationsFilterPanel'
import {
  Body,
  Container,
  DropdownButton,
  Header,
  SubHeader,
  ContentWrapper,
} from './styles'
import { useSelector } from 'react-redux'
import NotificationCard from './components/NotificationCard'
import { CommaArrayParam, useQueryParamsReplace } from '~/hooks/useQueryParams'
import { LocationsFilterContext } from '~/components/LocationsFilters/LocationNamesFilterWithParams'
import { NumberParam, StringParam, withDefault } from 'use-query-params'
import Pagination from '~/components/ecto/Pagination'
import NotificationModal from './components/NotificationModal'
import { toast } from 'react-toastify'

const MOBILE_MODE_BREAKPOINT = 1280

export const NotificationPageContext = React.createContext()

function DropdownPaginationSize({ loading, onChange, value, isMobile }) {
  const [hasChanged, setHasChanged] = useState(false)

  return (
    <Hide condition={isMobile}>
      <UncontrolledDropdown
        style={{ width: 'fit-content', zIndex: 10 }}
        disabled={loading}
      >
        <InputGroup className="position-relative form-label-group has-icon-right mb-0">
          <DropdownButton>
            <span style={{ color: '#333 !important' }} className="align-middle">
              <FormattedMessage id={hasChanged ? value : 'Page size'} />
            </span>
          </DropdownButton>
          <div
            style={{ pointerEvents: 'none' }}
            className="input-icon-right form-control-position"
          >
            <ChevronDown size={18} />
          </div>
        </InputGroup>

        <DropdownMenu tag="div" right>
          {[25, 50, 100].map(option => (
            <DropdownItem
              key={option}
              tag="a"
              onClick={() => {
                setHasChanged(true)
                onChange(option)
              }}
              className={classnames({
                'bg-primary text-white': option === value,
              })}
            >
              {option}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    </Hide>
  )
}

function Notifications() {
  const [isVisibleSideMenu, setIsVisibleSideMenu] = useState(false)
  const [fetchingNotifications, setFetchingNotifications] = useState(true)
  const [type, setType] = useState()
  const [isOpen, setIsOpen] = useState(false)
  const [modalData, setModalData] = useState({})
  const [isFetching, setIsFetching] = useState(false)

  const theme = useTheme()
  const { width } = useWindowDimensions()
  const projects = useSelector(state => state.user?.profile?.projects || [])
  const projectId = useSelector(state => state.user?.selectedProject?.id)

  const [queryParams, setQueryParams] = useQueryParamsReplace({
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 25),
    projects: withDefault(StringParam, 'all'),
    types: withDefault(CommaArrayParam, ['all']),
    id: StringParam,
  })

  const isMobileMode = useMemo(() => width < MOBILE_MODE_BREAKPOINT, [width])

  const intl = useIntl()

  const [{ data, loading: fetching }, fetchNotifications] = useAxios(
    {
      method: 'POST',
      url: `/projects/${projectId}/notifications`,
    },
    { useCache: false, manual: true }
  )

  const [, fetchNotificationData] = useAxios(
    {
      method: 'GET',
      url: `/projects/${projectId}/notifications/:notificationId/show`,
    },
    { useCache: false, manual: true }
  )

  const batchGetBodyRev = useRef({})

  batchGetBodyRev.current = {
    limit: queryParams?.limit,
    page: queryParams?.page,
    status: queryParams?.status,
    projects: [],
    types: queryParams?.types,
    id: queryParams?.id,
  }

  const handleFetchNotifications = useCallback(
    _.debounce((projects, gmbAccountIds) => {
      const data = {
        ...batchGetBodyRev.current,
        projects,
        gmbAccountIds,
      }

      fetchNotifications({
        data,
      }).finally(_ => setFetchingNotifications(false))
    }, 250),
    [fetchNotifications]
  )

  const scrollToTopList = () => {
    window.scrollTo({ top: 75, behavior: 'smooth' })
  }

  const closeModal = React.useCallback(() => {
    setIsOpen(false)
    setModalData({})
  }, [setModalData])

  const fetchDataAndOpenModal = React.useCallback(
    async (id, typeNotification) => {
      try {
        setIsFetching(true)
        const { data } = await fetchNotificationData({
          url: `/projects/${projectId}/notifications/${id}/show`,
        })

        if (!data) throw new Error('Notification data not found.')

        const clipboardLink = `${window.location.origin.toString()}/notifications?id=${id}`

        setModalData({
          data,
          type: typeNotification,
          clipboardLink,
        })
        setIsOpen(true)
      } catch (error) {
        toast.error('Notification data not found.')
      } finally {
        setIsFetching(false)
      }
    },
    [fetchNotificationData, projectId]
  )

  useEffect(() => {
    const selectedProjects =
      queryParams.projects === 'all'
        ? projects?.map(p => p.id)
        : projects
            .filter(p => queryParams.projects.includes(p.gmbAccountId))
            .map(p => p.id)

    const selectedGmbAccountIds =
      queryParams.projects === 'all'
        ? projects.map(p => p.gmbAccountId)
        : projects
            ?.filter(p => queryParams.projects.includes(p.gmbAccountId))
            .map(p => p.gmbAccountId)
    handleFetchNotifications(selectedProjects, selectedGmbAccountIds)
  }, [queryParams, handleFetchNotifications, projects])

  return (
    <>
      <Header>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link to="/">
                  <Home size={20} />
                </Link>
              ),
            },
            { text: 'Notifications' },
          ]}
        />
      </Header>
      <ContentWrapper>
        <Show condition={isMobileMode}>
          <div className="mb-1 p-1 bg-white">
            <Checkbox
              color="primary"
              label="Google update"
              defaultChecked={false}
            />
          </div>
          {/* TODO: Select For MOBILE */}
        </Show>

        <Container>
          <LocationsFilterContext.Provider
            value={{
              queryParams,
              setQueryParams,
              scrollToTop: scrollToTopList,
            }}
          >
            <NotificationsFilterPanel
              isVisibleSideMenu={isVisibleSideMenu}
              setIsVisibleSideMenu={setIsVisibleSideMenu}
              isMobileMode={isMobileMode}
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          </LocationsFilterContext.Provider>
          <NotificationPageContext.Provider
            value={{
              type,
              setType,
              closeModal,
              isOpen,
              setIsOpen,
              modalData,
              setModalData,
              isFetching,
              setIsFetching,
              fetchDataAndOpenModal,
            }}
          >
            <Body>
              <SubHeader>
                <strong>
                  <Show
                    condition={
                      !_.isEmpty(data?.notifications) &&
                      !fetching &&
                      !fetchingNotifications
                    }
                  >
                    <FormattedMessage
                      id="Found Notifications"
                      values={{
                        totalCount: intl.formatNumber(data?.total || 0),
                      }}
                    />
                  </Show>
                  <Show condition={fetching && fetchingNotifications}>
                    <FormattedMessage id="Fetching Notifications..." />
                  </Show>
                </strong>
                <div className="align-items-center">
                  <DropdownPaginationSize
                    value={queryParams.limit}
                    onChange={limit =>
                      setQueryParams({ ...queryParams, limit })
                    }
                    isMobile={isMobileMode}
                  />

                  <Show condition={isMobileMode}>
                    <button
                      className="btn btn-link p-0"
                      style={{ backgroundColor: 'transparent' }}
                      onClick={() => setIsVisibleSideMenu(true)}
                    >
                      <Filter color={theme.color.info} size={25} />
                    </button>
                  </Show>
                </div>
              </SubHeader>

              <div className="d-flex flex-column mt-2">
                {queryParams.id && (
                  <div>
                    <Button
                      color="success"
                      className="w-auto mb-1"
                      onClick={() =>
                        setQueryParams(prev => ({ ...prev, id: null }))
                      }
                    >
                      <FormattedMessage id="Return to listing" />
                    </Button>
                  </div>
                )}

                <Show
                  condition={
                    _.isEmpty(data?.notifications) &&
                    !fetching &&
                    !fetchingNotifications
                  }
                >
                  <span className="text-center">
                    <FormattedMessage id="No results found" />
                  </span>
                </Show>

                <Iterate
                  data={data?.notifications}
                  keyExtractor={(_, idx) => idx}
                  render={NotificationCard}
                />

                <Show condition={fetching}>
                  <Spinner style={{ margin: '0 auto' }} color="primary" />
                </Show>
                <Show condition={!fetching && data?.notifications?.length}>
                  <Pagination
                    showLimitPerPage
                    onPageChange={() => scrollToTopList()}
                    onLimitChange={() => scrollToTopList()}
                    total={data?.total}
                  />
                </Show>
              </div>
            </Body>
            <NotificationModal />
          </NotificationPageContext.Provider>
        </Container>
      </ContentWrapper>
    </>
  )
}

export default Notifications
